import React, { useState, useEffect,useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { 
  ArrowUpRight, ArrowDownLeft, Search, Calendar, 
  Download, ChevronLeft, ChevronRight, AlertCircle,
  Clock, Receipt, Share2, FileText
} from "lucide-react";

import { useReactToPrint } from 'react-to-print';
import { useAuth } from "../context/AuthContext";
import { formatCurrency, formatDateTime, cn } from "../lib/utils";
import axios from "axios";

import { toast } from "sonner";
const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Transactions = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [page, setPage] = useState(1);
  const perPage = 10;

  useEffect(() => {
    fetchTransactions();
  }, []);

  useEffect(() => {
    filterTransactions();
  }, [transactions, search, typeFilter]);

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(`${API_URL}/transactions`);
      setTransactions(response.data);
    } catch (error) {
      console.error("Failed to fetch transactions:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterTransactions = () => {
    let filtered = [...transactions];
    if (search) {
      filtered = filtered.filter(
        (txn) =>
          txn.beneficiary_name?.toLowerCase().includes(search.toLowerCase()) ||
          txn.narration?.toLowerCase().includes(search.toLowerCase()) ||
          txn.id.toLowerCase().includes(search.toLowerCase())
      );
    }
    if (typeFilter !== "all") {
      filtered = filtered.filter((txn) => txn.transaction_type === typeFilter);
    }
    setFilteredTransactions(filtered);
    setPage(1);
  };

  const paginatedTransactions = filteredTransactions.slice(
    (page - 1) * perPage,
    page * perPage
  );

  const totalPages = Math.ceil(filteredTransactions.length / perPage);

  return (
    <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700" data-testid="transactions-page">
      
      {/* Header & Filter Section */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h1 className="text-xl font-black tracking-tight text-slate-900">Transaction History</h1>
        </div>

        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-slate-900 transition-colors" />
            <Input
              placeholder="Search history..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-11 h-12 w-full md:w-64 rounded-2xl bg-slate-100/50 text-sm  focus-visible:ring-2 focus-visible:ring-slate-900 border"
              data-testid="search-input"
            />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-full md:w-40 h-12 rounded-2xl bg-slate-100/50 font-black text-[12px] uppercase tracking-widest" data-testid="type-filter">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent className="rounded-2xl border-none shadow-xl ">
              <SelectItem value="all" className="font-bold text-sm">ALL ACTIVITY</SelectItem>
              <SelectItem value="credit" className="font-bold text-sm">CREDITS ONLY</SelectItem>
              <SelectItem value="debit" className="font-bold text-sm">DEBITS ONLY</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Transactions Table/List */}
      <Card className="rounded-[2.5rem] border-slate-100 shadow-sm overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-20 bg-slate-50 rounded-[1.5rem] animate-pulse" />
              ))}
            </div>
          ) : paginatedTransactions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mb-4">
                <Clock className="w-8 h-8 text-slate-300" />
              </div>
              <p className="font-black text-slate-400 uppercase text-sm tracking-widest">No history found</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {paginatedTransactions.map((txn) => (
                <div
                  key={txn.id}
                  onClick={() => navigate(`/transactions/${txn.id}`)}
                  className="group flex items-center justify-between p-6 hover:bg-slate-50 transition-all cursor-pointer"
                  data-testid={`transaction-item-${txn.id}`}
                >
                  <div className="flex items-center gap-5">
                    <div
                      className={cn(
                        "w-14 h-14 rounded-2xl flex items-center justify-center transition-transform group-hover:scale-110",
                        txn.transaction_type === "credit"
                          ? "bg-emerald-50 text-emerald-600"
                          : "bg-red-50 text-red-600"
                      )}
                    >
                      {txn.transaction_type === "credit" ? (
                        <ArrowDownLeft className="w-6 h-6" />
                      ) : (
                        <ArrowUpRight className="w-6 h-6" />
                      )}
                    </div>
                    <div>
                      <p className="font-black text-slate-900 group-hover:underline underline-offset-4 text-sm decoration-2">
                        {txn.beneficiary_name || txn.narration}
                      </p>
                      <div className="flex items-center gap-3 mt-1">
                        <p className="text-[12px] font-bold text-slate-400 uppercase tracking-tighter">
                          {formatDateTime(txn.created_at)}
                        </p>
                        <span className="w-1 h-1 rounded-full bg-slate-200" />
                      
                      </div>
                    </div>
                  </div>

                  <div className="text-right">
                    <p className={cn(
                      "font-black text-sm tabular-nums tracking-tighter",
                      txn.transaction_type === "credit" ? "text-emerald-600" : "text-slate-900"
                    )}>
                      {txn.transaction_type === "credit" ? "+" : "-"}
                      {formatCurrency(txn.amount, user?.currency)}
                    </p>
                    <div className="mt-1">
                      {txn.status === "success" ? (
                        <span className="px-2 py-0.5 bg-emerald-50 text-emerald-600 text-[7px] font-black uppercase tracking-widest rounded-md border border-emerald-100">
                          Success
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 bg-red-50 text-red-600 text-[7px] font-black uppercase tracking-widest rounded-md border border-red-100">
                          Failed
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-8 bg-slate-50/50 border-t border-slate-100">
              <p className="text-[12px] font-black text-slate-400 uppercase tracking-widest">
                Page {page} / {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-xl font-bold bg-white border-none shadow-sm h-10 w-10 p-0"
                  onClick={(e) => { e.stopPropagation(); setPage((p) => Math.max(1, p - 1)); }}
                  disabled={page === 1}
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-xl font-bold bg-white border-none shadow-sm h-10 w-10 p-0"
                  onClick={(e) => { e.stopPropagation(); setPage((p) => Math.min(totalPages, p + 1)); }}
                  disabled={page === totalPages}
                  data-testid="next-page-btn"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export const TransactionDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTransaction();
  }, [id]);

  const fetchTransaction = async () => {
    try {
      const response = await axios.get(`${API_URL}/transactions/${id}`);
      setTransaction(response.data);
    } catch (error) {
      console.error("Failed to fetch transaction:", error);
    } finally {
      setLoading(false);
    }
  };

  const contentRef = useRef(null);

  // 2. Initialize the print function
  const handlePrint = useReactToPrint({
    contentRef: contentRef, // This MUST match the ref below
    documentTitle: `Receipt_${transaction?.id?.slice(0, 8)}`,
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-10 h-10 animate-spin text-slate-300" />
      </div>
    );
  }

  if (!transaction) {
    return (
      <div className="text-center py-20 max-w-sm mx-auto">
        <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6">
          <AlertCircle className="w-10 h-10 text-red-500" />
        </div>
        <h3 className="text-xl font-black text-slate-900 uppercase tracking-tighter">Transaction Not Found</h3>
        <Button onClick={() => navigate("/transactions")} className="mt-6 rounded-2xl bg-slate-900 font-black uppercase tracking-widest text-sm h-12 w-full">
          Return to activity
        </Button>
      </div>
    );
  }

  const isCredit = transaction.transaction_type === "credit";
  const isSuccess = transaction.status === "success";

  return (
    <div className="max-w-md mx-auto space-y-6 pb-20 animate-in fade-in zoom-in-95 duration-500"ref={contentRef} data-testid="transaction-details">
      <Button
        variant="ghost"
        onClick={() => navigate("/transactions")}
        className="group text-slate-400 hover:text-slate-900 hover:bg-transparent -ml-4"
        data-testid="back-btn"
      >
        <ChevronLeft className="w-5 h-5 mr-1 group-hover:-translate-x-1 transition-transform" />
        <span className="font-black uppercase text-[12px] tracking-widest">Back to Transactions</span>
      </Button>

      <div className="relative">
        {/* Receipt Visuals */}
        <div className={cn(
          "rounded-t-[3rem] p-5 text-center relative overflow-hidden",
          !isSuccess ? "bg-red-600 text-white" : isCredit ? "bg-emerald-600 text-white" : "bg-slate-900 text-white"
        )}>
          {/* Decorative pattern */}
          <div className="absolute inset-0 opacity-10 pointer-events-none" 
            style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '24px 24px' }} 
          />
          
          <div className="relative z-10">
            <div className="w-16 h-16 rounded-3xl bg-white/20 backdrop-blur-md flex items-center justify-center mx-auto mb-6 shadow-2xl">
              {!isSuccess ? <AlertCircle size={32} /> : isCredit ? <ArrowDownLeft size={32} /> : <ArrowUpRight size={32} />}
            </div>
            <p className="text-white/70 font-black uppercase text-[12px] tracking-[0.3em] mb-2">
              {isCredit ? "Funds Received" : "Payment Sent"}
            </p>
            <p className="text-3xl font-black tracking-tighter tabular-nums">
              {isCredit ? "+" : "-"}{formatCurrency(transaction.amount, user?.currency)}
            </p>
          </div>
        </div>

        {/* Info Card */}
        <Card className="rounded-b-[3rem] rounded-t-none border-none shadow-2xl shadow-slate-200 -mt-2">
          <CardContent className="p-8 space-y-6 ">
            <div className="grid grid-cols-1 gap-6">
              <DetailItem label="Status" value={isSuccess ? "Success" : "Failed"} 
                valueClass={isSuccess ? "text-emerald-600" : "text-red-600"} 
              />
              <DetailItem label="Transaction ID" value={transaction.id.toUpperCase()} isMono />
              <DetailItem label="Date & Time" value={formatDateTime(transaction.created_at)} />
              <DetailItem label="Beneficiary" value={transaction.beneficiary_name} />
              {transaction.beneficiary_bank && (
                <DetailItem label="Bank Name" value={transaction.beneficiary_bank} />
              )}
              {transaction.service_charge > 0 && (
                <DetailItem label="Service Fee" value={formatCurrency(transaction.service_charge, user?.currency)} />
              )}
              {transaction.narration && (
                <DetailItem label="Remark" value={transaction.narration} />
              )}
            </div>

            <div className="pt-6 grid grid-cols-2 gap-3">
              <Button
                onClick={() => handlePrint(transaction.id)}
                className="rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-900 font-black uppercase text-[12px] tracking-widest h-14 border-none"
                data-testid="download-pdf-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                PDF
              </Button>
              <Button
                variant="outline"
                onClick={handlePrint}
                className="rounded-2xl border-2 border-slate-100 font-black uppercase text-[12px] tracking-widest h-14"
                data-testid="download-image-btn"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <p className="text-center text-[12px] font-black text-slate-300 uppercase tracking-widest pt-4">
        Verified Transaction • Secured by AES-256
      </p>
    </div>
  );
};

// Helper component for detail rows
const DetailItem = ({ label, value, isMono = false, valueClass = "text-slate-900" }) => (
  <div className="flex flex-col gap-1">
    <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">{label}</span>
    <span className={cn(
      "font-bold text-sm truncate",
      isMono ? "font-mono text-sm uppercase" : "",
      valueClass
    )}>
      {value}
    </span>
  </div>
);

const Loader2 = ({ className }) => <div className={cn("border-4 border-slate-200 border-t-slate-900 rounded-full animate-spin", className)} />;